import React, { useState } from 'react';
import { Mail, MapPin, Send, Github, Linkedin, Twitter, CheckCircle, Globe, MessageSquare } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { useTheme } from '../ThemeContext';
import { useData } from '../DataContext';

const Contact = () => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const { contact, messages, loading } = useData();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    projectType: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  if (loading || !contact.data) {
    return (
      <section className="py-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </section>
    );
  }

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await messages.create({
        ...formData,
        timestamp: Date.now(),
        read: false
      });
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        projectType: ''
      });
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <Mail className="w-6 h-6" />,
      titleKey: "email",
      content: contact.data?.email || "salih@ai-engineer.com",
      link: `mailto:${contact.data?.email || "salih@ai-engineer.com"}`
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      titleKey: "location",
      content: contact.data?.location || "Istanbul, Turkey",
      link: "#"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      titleKey: "working",
      contentKey: "workingValue",
      content: contact.getLocalized('availability', language) || t.contact.workingValue,
      link: "#"
    }
  ];

  const social = contact.data?.social || {};
  const socialLinks = [
    { icon: <Github className="w-5 h-5" />, name: 'GitHub', url: social.github || '#', color: 'hover:bg-gray-700' },
    { icon: <Linkedin className="w-5 h-5" />, name: 'LinkedIn', url: social.linkedin || '#', color: 'hover:bg-blue-600' },
    { icon: <Twitter className="w-5 h-5" />, name: 'Twitter', url: social.twitter || '#', color: 'hover:bg-sky-500' },
    { icon: <MessageSquare className="w-5 h-5" />, name: 'Discord', url: social.discord || '#', color: 'hover:bg-purple-600' }
  ];

  if (isSubmitted) {
    return (
      <section id="contact" className={`py-24 relative overflow-hidden ${isDark ? 'bg-[#0a0f1a]' : 'bg-white'}`}>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-2xl mx-auto text-center">
            <div className="mb-8">
              <div className="w-24 h-24 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
              <h2 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                {t.contact.messageSent}
              </h2>
              <p className={`text-lg font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.contact.thankYou}
              </p>
            </div>
            <button
              onClick={() => setIsSubmitted(false)}
              className="px-8 py-3 bg-blue-600 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300"
            >
              {t.contact.sendAnother}
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className={`py-24 relative overflow-hidden ${isDark ? 'bg-[#0a0f1a]' : 'bg-white'}`}>
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className={`absolute top-1/4 left-0 w-96 h-96 ${isDark ? 'bg-blue-500/5' : 'bg-blue-200/20'} rounded-full blur-[120px]`}></div>
        <div className={`absolute bottom-1/4 right-0 w-96 h-96 ${isDark ? 'bg-blue-500/5' : 'bg-blue-200/20'} rounded-full blur-[120px]`}></div>

        {/* Grid Pattern */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: isDark
              ? `linear-gradient(rgba(59, 130, 246, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.08) 1px, transparent 1px)`
              : `linear-gradient(rgba(148, 163, 184, 0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(148, 163, 184, 0.15) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}
        ></div>

        {/* Horizontal Sparkles */}
        <div className={`contact-sparkle-h1 absolute w-20 h-[2px] bg-gradient-to-r from-transparent ${isDark ? 'via-blue-400' : 'via-blue-400'} to-transparent`} style={{ top: '25%' }}></div>
        <div className={`contact-sparkle-h2 absolute w-24 h-[2px] bg-gradient-to-r from-transparent ${isDark ? 'via-cyan-400' : 'via-cyan-400'} to-transparent`} style={{ top: '50%' }}></div>
        <div className={`contact-sparkle-h3 absolute w-16 h-[2px] bg-gradient-to-r from-transparent ${isDark ? 'via-indigo-400' : 'via-indigo-400'} to-transparent`} style={{ top: '75%' }}></div>

        {/* Vertical Sparkles */}
        <div className={`contact-sparkle-v1 absolute w-[2px] h-20 bg-gradient-to-b from-transparent ${isDark ? 'via-blue-400' : 'via-blue-400'} to-transparent`} style={{ left: '20%' }}></div>
        <div className={`contact-sparkle-v2 absolute w-[2px] h-24 bg-gradient-to-b from-transparent ${isDark ? 'via-cyan-400' : 'via-cyan-400'} to-transparent`} style={{ left: '50%' }}></div>
        <div className={`contact-sparkle-v3 absolute w-[2px] h-16 bg-gradient-to-b from-transparent ${isDark ? 'via-indigo-400' : 'via-indigo-400'} to-transparent`} style={{ left: '80%' }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className={`font-light tracking-widest uppercase mb-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{t.contact.sectionTitle}</p>
          <h2 className={`text-4xl md:text-5xl lg:text-6xl font-black mb-6 ${isDark ? 'text-white' : 'text-slate-800'}`}>
            {t.contact.title1}
            <span className="text-blue-500"> {t.contact.title2}</span>
          </h2>
          <p className={`text-lg max-w-2xl mx-auto font-light leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Info Cards */}
            <div className="space-y-4">
              {contactInfo.map((info, index) => (
                <a
                  key={index}
                  href={info.link}
                  className={`flex items-center gap-4 p-5 rounded-2xl border transition-all duration-300 group ${
                    isDark
                      ? 'bg-white/5 border-white/5 hover:border-blue-500/30'
                      : 'bg-slate-50 border-slate-200 hover:border-blue-400 shadow-sm hover:shadow-md'
                  }`}
                >
                  <div className="p-3 bg-blue-500/20 rounded-xl text-blue-400 group-hover:scale-110 transition-transform duration-300">
                    {info.icon}
                  </div>
                  <div>
                    <p className={`text-sm font-light ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{t.contact[info.titleKey]}</p>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{info.content || t.contact[info.contentKey]}</p>
                  </div>
                </a>
              ))}
            </div>

            {/* Social Links */}
            <div>
              <p className={`text-sm mb-4 font-light ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{t.contact.connectWith}</p>
              <div className="flex gap-3">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    className={`p-3 rounded-xl transition-all duration-300 hover:scale-110 ${
                      isDark
                        ? `bg-white/5 text-gray-400 hover:text-white ${social.color}`
                        : `bg-slate-100 text-slate-600 hover:text-white ${social.color}`
                    }`}
                    title={social.name}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Availability */}
            <div className={`p-6 rounded-2xl border ${isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className="w-3 h-3 bg-emerald-400 rounded-full animate-pulse"></span>
                <span className="text-emerald-400 font-medium">{t.contact.available}</span>
              </div>
              <p className={`text-sm font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.contact.availableDesc}
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className={`space-y-6 p-8 rounded-3xl border ${
              isDark
                ? 'bg-white/5 border-white/5'
                : 'bg-slate-50 border-slate-200 shadow-md'
            }`}>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm mb-2 font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t.contact.yourName}</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-3 border rounded-xl focus:border-blue-500 focus:outline-none transition-colors duration-300 ${
                      isDark
                        ? 'bg-white/5 border-white/10 text-white placeholder-gray-500'
                        : 'bg-white border-slate-200 text-slate-800 placeholder-slate-400'
                    }`}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className={`block text-sm mb-2 font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t.contact.yourEmail}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-3 border rounded-xl focus:border-blue-500 focus:outline-none transition-colors duration-300 ${
                      isDark
                        ? 'bg-white/5 border-white/10 text-white placeholder-gray-500'
                        : 'bg-white border-slate-200 text-slate-800 placeholder-slate-400'
                    }`}
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div>
                <label className={`block text-sm mb-2 font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t.contact.projectType}</label>
                <select
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-xl focus:border-blue-500 focus:outline-none transition-colors duration-300 appearance-none cursor-pointer ${
                    isDark
                      ? 'bg-white/5 border-white/10 text-white'
                      : 'bg-white border-slate-200 text-slate-800'
                  }`}
                >
                  <option value="" className={isDark ? 'bg-[#0f172a]' : 'bg-white'}>{t.contact.selectProjectType}</option>
                  {t.contact.projectTypes.map((type, index) => (
                    <option key={index} value={type} className={isDark ? 'bg-[#0f172a]' : 'bg-white'}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm mb-2 font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t.contact.subject}</label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-3 border rounded-xl focus:border-blue-500 focus:outline-none transition-colors duration-300 ${
                    isDark
                      ? 'bg-white/5 border-white/10 text-white placeholder-gray-500'
                      : 'bg-white border-slate-200 text-slate-800 placeholder-slate-400'
                  }`}
                  placeholder={t.contact.subjectPlaceholder}
                />
              </div>

              <div>
                <label className={`block text-sm mb-2 font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t.contact.message}</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="5"
                  className={`w-full px-4 py-3 border rounded-xl focus:border-blue-500 focus:outline-none transition-colors duration-300 resize-none ${
                    isDark
                      ? 'bg-white/5 border-white/10 text-white placeholder-gray-500'
                      : 'bg-white border-slate-200 text-slate-800 placeholder-slate-400'
                  }`}
                  placeholder={t.contact.messagePlaceholder}
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    {t.contact.sendMessage}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
